import React, { useMemo } from 'react';
import { RotateCcw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ScriptEdit } from '@/types/rebuild';

interface RebuildChangesPanelProps {
  edits: ScriptEdit[];
  onToggle: (editId: string, status: 'applied' | 'reverted') => void;
  onJumpTo: (editId: string) => void;
}

export function RebuildChangesPanel({ edits, onToggle, onJumpTo }: RebuildChangesPanelProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, ScriptEdit[]>();
    for (const e of edits) {
      const list = map.get(e.metric_key) ?? [];
      list.push(e);
      map.set(e.metric_key, list);
    }
    return map;
  }, [edits]);

  if (!edits.length) {
    return (
      <p className="text-sm text-white/45 font-sans py-8 text-center">
        No AI edits were generated. Try selecting different recommendations.
      </p>
    );
  }

  return (
    <div className="space-y-4 overflow-y-auto max-h-[560px] pr-1">
      {Array.from(grouped.entries()).map(([metric, items]) => (
        <div key={metric} className="border border-[#2a2a2a] rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-[#141414] border-b border-[#2a2a2a]">
            <h4 className="text-xs font-sans font-semibold text-gold-button uppercase tracking-wider">
              {metric}
            </h4>
          </div>
          <ul className="divide-y divide-[#2a2a2a]">
            {items.map((edit) => {
              const applied = edit.status === 'applied';
              return (
                <li key={edit.id} className="p-3 bg-[#0d0d0d]">
                  <p className="text-xs text-white/50 font-sans mb-2 line-clamp-2">
                    {edit.recommendation_text}
                  </p>
                  <div className="text-[11px] font-mono text-white/40 mb-2 line-through decoration-red-400/50">
                    {edit.original_text.slice(0, 120)}
                    {edit.original_text.length > 120 ? '…' : ''}
                  </div>
                  <div className="text-[11px] font-mono text-amber-200/70 mb-3">
                    {edit.revised_text.slice(0, 120)}
                    {edit.revised_text.length > 120 ? '…' : ''}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-[#2a2a2a] text-white/70 hover:bg-white/5 font-sans"
                      onClick={() => onJumpTo(edit.id)}
                    >
                      Jump to
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-[#2a2a2a] font-sans"
                      onClick={() => onToggle(edit.id, applied ? 'reverted' : 'applied')}
                    >
                      {applied ? (
                        <>
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Revert
                        </>
                      ) : (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          Apply
                        </>
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
